import { RepoType, MasterLegalAgreementType, EntityType, SettlementVenue, MarketType } from 'api'
import { createSelector, Selector } from '@reduxjs/toolkit'
import { instrumentsAreAllUSMarketGetterSelector } from 'selectors/collateralSelectors'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { Nullable } from 'utils'
import { isNotNil } from 'utils/predicate'
import {
  createTradingEntityHasTradingEntityClearingRelationshipGetterSelector,
  createTradingEntityHasSellSideUsClearingRelationshipWithFundsSelector,
  createTradingEntityHasSellSideRelationshipWithFundsSelector,
  tradingEntityIsStandardType,
  tradingEntityHasRepoTypeSBLGetterSelector,
  tradingEntityIsSellSideFilterSelector,
  createTradingEntityDoesNotBelongToFunds,
  createTradingEntityBelongsToOrganisation,
  createTradingEntityCanTradeCollaterals,
  createTradingEntityCanTradeCollateralBasketsSelector
} from './tradingEntityFilterSelectors'

const createSettlementVenueIsSet = (settlementVenue: Nullable<SettlementVenue>) => () => isNotNil(settlementVenue)

export interface CounterpartyFilter {
  isin: string | null
  bui: string | null
  secondaryCollateralId: string | null
  secondaryBasketId: string | null
  fundId: string | null
  repoType: RepoType | null
  masterLegalAgreementType: MasterLegalAgreementType | null
  marketType: MarketType | null
}

export interface CounterpartyFilterSelectorProps {
  filters: CounterpartyFilter[]
  settlementVenue: SettlementVenue | null
  clearingHouseId: string | null
  organisationId: string | null
}

export const createTradingEntityFiltersForCounterpartyListSelector = <TProps>(
  filtersSelector: Selector<{}, CounterpartyFilter[], [TProps]>,
  settlementVenueSelector: Selector<{}, Nullable<SettlementVenue>, [TProps]>,
  clearingHouseIdSelector: Selector<{}, string | null, [TProps]>,
  organisationIdSelector: Selector<{}, Nullable<string>, [TProps]>
) =>
  createSelector(
    [
      filtersSelector,
      settlementVenueSelector,
      clearingHouseIdSelector,
      organisationIdSelector,
      createTradingEntityHasSellSideRelationshipWithFundsSelector,
      createTradingEntityHasSellSideUsClearingRelationshipWithFundsSelector,
      tradingEntityIsSellSideFilterSelector,
      createEntityGetterSelector(EntityType.COLLATERAL),
      createEntityGetterSelector(EntityType.COLLATERAL_BASKET),
      instrumentsAreAllUSMarketGetterSelector,
      createTradingEntityHasTradingEntityClearingRelationshipGetterSelector,
      tradingEntityHasRepoTypeSBLGetterSelector,
      createTradingEntityCanTradeCollateralBasketsSelector
    ],
    (
      filters,
      settlementVenue,
      clearingHouseId,
      organisationId,
      createTradingEntityHasSellSideRelationshipWithFunds,
      createTradingEntityHasSellSideUsClearingRelationshipWithFunds,
      tradingEntityIsSellSide,
      getCollateral,
      getCollateralBasket,
      instrumentsAreAllUSMarket,
      createTradingEntityHasTradingEntityClearingRelationship,
      tradingEntityHasRepoTypeSBL,
      createTradingEntityCanTradeCollateralBaskets
    ) => {
      const filterDetails = filters.map(filter => ({
        ...filter,
        collateral: getCollateral(filter.isin),
        collateralBasket: getCollateralBasket(filter.bui),
        secondaryCollateral: getCollateral(filter.secondaryCollateralId),
        secondaryCollateralBasket: getCollateralBasket(filter.secondaryBasketId)
      }))

      const fundIds = filterDetails.map(filter => filter.fundId).filter(isNotNil)
      const collaterals = filterDetails.map(filter => filter.collateral).filter(isNotNil)
      const collateralBaskets = filterDetails.map(filter => filter.collateralBasket).filter(isNotNil)
      const masterLegalAgreementTypes = filterDetails.map(filter => filter.masterLegalAgreementType).filter(isNotNil)
      const repoTypes = filterDetails.map(filter => filter.repoType).filter(isNotNil)

      const collateralsWithMarkets = filterDetails.flatMap(f =>
        f.collateral && f.marketType ? [{ collateral: f.collateral, marketType: f.marketType }] : []
      )

      const basketsWithMarkets = filterDetails.flatMap(f =>
        f.collateralBasket && f.marketType ? [{ collateralBasket: f.collateralBasket, marketType: f.marketType }] : []
      )

      const secondaryCollateralsWithMarkets = filterDetails.flatMap(f =>
        f.secondaryCollateral && f.marketType ? [{ collateral: f.secondaryCollateral, marketType: f.marketType }] : []
      )

      const secondaryBasketsWithMarkets = filterDetails.flatMap(f =>
        f.secondaryCollateralBasket && f.marketType ? [{ collateralBasket: f.secondaryCollateralBasket, marketType: f.marketType }] : []
      )

      return [
        createSettlementVenueIsSet(settlementVenue),
        tradingEntityIsStandardType,
        tradingEntityIsSellSide,
        createTradingEntityDoesNotBelongToFunds(fundIds),
        ...(isNotNil(clearingHouseId)
          ? [
              ...(instrumentsAreAllUSMarket([...collaterals, ...collateralBaskets])
                ? [
                    createTradingEntityHasSellSideUsClearingRelationshipWithFunds(fundIds, masterLegalAgreementTypes),
                    createTradingEntityCanTradeCollaterals(collateralsWithMarkets),
                    createTradingEntityCanTradeCollateralBaskets(basketsWithMarkets)
                  ]
                : [
                    createTradingEntityHasTradingEntityClearingRelationship(clearingHouseId, collaterals, collateralBaskets),
                    ...(isNotNil(organisationId) ? [createTradingEntityBelongsToOrganisation(organisationId)] : [])
                  ])
            ]
          : [
              createTradingEntityHasSellSideRelationshipWithFunds(fundIds, masterLegalAgreementTypes),
              createTradingEntityCanTradeCollateralBaskets(basketsWithMarkets),
              createTradingEntityCanTradeCollaterals(collateralsWithMarkets)
            ]),
        ...(repoTypes && repoTypes.some(repoType => repoType === RepoType.STOCK_BORROWING_LENDING)
          ? [
              tradingEntityHasRepoTypeSBL,
              createTradingEntityCanTradeCollateralBaskets(secondaryBasketsWithMarkets),
              createTradingEntityCanTradeCollaterals(secondaryCollateralsWithMarkets)
            ]
          : [])
      ]
    }
  )
